import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import { Col, Row } from "../../common/components/Grid";
import { changePassword, updateUser, useCurrentUser } from "../../reducers/userSlice";
import Label from "../../common/components/Label";
import TimezoneInput from "../../common/components/TimezoneInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import EmailInput from "../../common/components/EmailInput";
import { useMemo, useRef, useState } from "react";
import PermissionsInput from "./PermissionsInput";
import { DefaultButton } from "../../common/components/DefaultButton";
import useNetwork from "../../common/hooks/networkHook";
import UserSettings from "../UserSettings";
import useCredentials from "../../common/hooks/credentialsHook";
import { PasswordResetCard } from "../../common/components/PasswordReset";
import ReactForm from "../../common/components/ReactForm";

const UserSettingsPanel = () => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const clientConfig = useClientConfig()
    const network = useNetwork();
    const currentUser = useCurrentUser();
    const [delta, setDelta] = useState({});
    const formRef = useRef();

    const canSetPassword = useMemo(() => {
        const emailDomain = currentUser?.email ? currentUser.email.split('@')[1] : null
        return !currentUser?.unmodifiable && (!emailDomain || (clientConfig.sso?.every(domain => domain.toLowerCase() !== emailDomain.toLowerCase()) ?? true))
    }, [clientConfig, currentUser?.unmodifiable, currentUser?.email]);

    const displayUser = useMemo(() => ({ ...currentUser, ...delta }), [currentUser, delta]);

    const isUpdated = useMemo(() => Object.keys(delta).length > 0, [delta]);

    const updateEmail = email => {
        setDelta(delta => ({ ...delta, email }));
    }

    const updateTimezone = tz => {
        setDelta(delta => ({ ...delta, tz }));
    }

    const updatePassword = (value) => {
        changePassword(network, value.currentPassword, value.password);
    }

    const saveChanges = () => {
        if (formRef.current.checkValidity()) {
            updateUser(network, { ...delta, username: credentials.username });
            setDelta({})
        }
    }

    const detailsFooterControls = <Row>
        <Col></Col>
        <Col xs='auto' className="pr-2"><DefaultButton disabled={!isUpdated} kind='clear' onClick={() => setDelta({})}></DefaultButton></Col>
        <Col xs='auto'><DefaultButton disabled={!isUpdated} kind='apply' onClick={saveChanges}></DefaultButton></Col>
    </Row>

    return  <>
        <Row>
            <Col className="text-end text-secondary">
                Build {process.env.APP_GIT_SHA || 'DEV'}
            </Col>
        </Row>
        <Card title={t('userDetails')} footerControls={detailsFooterControls}>
            <ReactForm ref={formRef}>
                <Row>
                    <Col><Label placeholder={t('firstname')} text={displayUser?.firstname}></Label></Col>
                    <Col><Label placeholder={t('lastname')} text={displayUser?.lastname}></Label></Col>
                </Row>
                <Row>
                    <Col><Label placeholder={t('username')} text={displayUser?.username}></Label></Col>
                    <Col><Label placeholder={t('title')} text={displayUser?.title ?? '-'} ></Label></Col>
                </Row>
                <Row>
                    <Col><EmailInput readonly={!canSetPassword} placeholder={t('email')} value={displayUser?.email} live required onChange={updateEmail}></EmailInput></Col>
                    <Col><Label placeholder={t('mobile')} text={displayUser?.mobile ?? '-'}></Label></Col>
                </Row>
                {clientConfig.timezones.enable ? <Row>
                    <Col><TimezoneInput placeholder={t('timezone')} value={displayUser?.tz ?? clientConfig.tz} live onChange={updateTimezone} required></TimezoneInput></Col>
                </Row> : null}
            </ReactForm>
        </Card>

        { canSetPassword && <PasswordResetCard requireCurrent={true} onSubmit={value => updatePassword(value)}></PasswordResetCard>}
        
        <PermissionsInput readonly title={t('role_other')} isRoleBased={true} values={displayUser?.role ?? []} access={[0, 1, 2]} adminPositions={['administrator', 'dispatcher', 'stats']} onApply={() => {}}></PermissionsInput>
        {clientConfig.sitrep.enable ? <PermissionsInput readonly title={t('sitrep')} values={displayUser?.sitrep_access ?? []} access={[0, 1]} adminPositions={[]} onApply={() => {}}></PermissionsInput> : null}
        {!clientConfig.trackerOnly && clientConfig.enableLog ? <PermissionsInput readonly title={t('log_one')} values={displayUser?.log_access ?? []} access={[0, 1]} adminPositions={[]} onApply={() => {}}></PermissionsInput> : null}
    
        <UserSettings username={credentials.username}></UserSettings>
    </>
}

export default UserSettingsPanel;